import React from 'react';
import PropTypes from 'prop-types';

import {Box, TextField, Typography} from 'views/components';
import {translate} from 'views/module';

const PhoneNumberInput = (props) => {
  const {questionData, value, onChange} = props;

  const [hasAppliedDefaultCountryCode, setHasAppliedDefaultCountryCode] = React.useState(false);

  const NUMBER_CHARACTERS_REGEX = /[^0-9]/g;
  const MAX_PHONE_DIGITS = 10;
  const MAX_COUNTRY_CODE_DIGITS = 3;

  React.useEffect(() => {
    if (!hasAppliedDefaultCountryCode && !value) {
      onChange('+1');
      setHasAppliedDefaultCountryCode(true);
    }
  }, [hasAppliedDefaultCountryCode, value, onChange]);

  const formatLocalPhoneNumber = React.useCallback((digits, hasOpenParen = false, hasCloseParen = false, hasHyphen = false) => {
    const trimmed = digits.slice(0, MAX_PHONE_DIGITS);
    if (!trimmed) return hasOpenParen ? '(' : '';

    if (trimmed.length <= 3) {
      const closeAreaCode = hasCloseParen && trimmed.length === 3;
      return `(${trimmed}${closeAreaCode ? ')' : ''}`;
    }

    if (trimmed.length <= 6) {
      const includeTrailingHyphen = hasHyphen && trimmed.length === 6;
      return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}${includeTrailingHyphen ? '-' : ''}`;
    }

    return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
  }, []);

  const buildInternationalPhoneValue = React.useCallback((countryCode, localDigits, hasOpenParen = false, hasCloseParen = false, hasHyphen = false) => {
    const normalizedCountryCode = (countryCode || '').slice(0, MAX_COUNTRY_CODE_DIGITS);
    const formattedLocal = formatLocalPhoneNumber(localDigits, hasOpenParen, hasCloseParen, hasHyphen);
    if (normalizedCountryCode && formattedLocal) return `+${normalizedCountryCode} ${formattedLocal}`;
    if (normalizedCountryCode) return `+${normalizedCountryCode}`;
    if (formattedLocal) return formattedLocal;
    return '';
  }, [formatLocalPhoneNumber]);

  const parsed = React.useMemo(() => {
    const current = value || '';
    let countryCode = '';
    let localDigits = '';
    let hasOpenParen = false;
    let hasCloseParen = false;
    let hasHyphen = false;

    if (current.startsWith('+')) {
      const countryCodeMatch = current.match(/^\+(\d{0,3})/);
      countryCode = countryCodeMatch?.[1] || '';
      const localRaw = current.slice(countryCodeMatch?.[0]?.length || 0).trimStart();
      hasOpenParen = localRaw.startsWith('(');
      hasCloseParen = localRaw.includes(')');
      hasHyphen = localRaw.includes('-');
      localDigits = localRaw.replace(NUMBER_CHARACTERS_REGEX, '').slice(0, MAX_PHONE_DIGITS);
    } else {
      const localRaw = current.trimStart();
      hasOpenParen = localRaw.startsWith('(');
      hasCloseParen = localRaw.includes(')');
      hasHyphen = localRaw.includes('-');
      const digitsOnly = localRaw.replace(NUMBER_CHARACTERS_REGEX, '');
      localDigits = digitsOnly.slice(0, MAX_PHONE_DIGITS);
    }

    return {
      countryCode: countryCode.slice(0, MAX_COUNTRY_CODE_DIGITS),
      localDigits,
      hasOpenParen,
      hasCloseParen,
      hasHyphen,
    };
  }, [MAX_COUNTRY_CODE_DIGITS, MAX_PHONE_DIGITS, NUMBER_CHARACTERS_REGEX, value]);

  const handleCountryCodeChange = (nextValue) => {
    const nextCountryCode = (nextValue || '').replace(NUMBER_CHARACTERS_REGEX, '').slice(0, MAX_COUNTRY_CODE_DIGITS);
    onChange(
        buildInternationalPhoneValue(
            nextCountryCode,
            parsed.localDigits,
            parsed.hasOpenParen,
            parsed.hasCloseParen,
            parsed.hasHyphen,
        ),
    );
  };

  const handleLocalNumberChange = (nextValue) => {
    const localRaw = (nextValue || '').trimStart();
    const hasOpenParen = localRaw.startsWith('(');
    const hasCloseParen = localRaw.includes(')') && localRaw.indexOf(')') >= 3;
    const hasHyphen = localRaw.includes('-') && localRaw.indexOf('-') >= 8;
    const nextLocalDigits = (nextValue || '').replace(NUMBER_CHARACTERS_REGEX, '').slice(0, MAX_PHONE_DIGITS);
    onChange(buildInternationalPhoneValue(parsed.countryCode, nextLocalDigits, hasOpenParen, hasCloseParen, hasHyphen));
  };

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid #DED8D7',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundColor: '#fff',
          height: '48px',
        }}
      >
        <TextField
          type='tel'
          value={parsed.countryCode}
          onChange={handleCountryCodeChange}
          sx={{
            'width': '5rem',
            'minWidth': '5rem',
            '& .MuiOutlinedInput-root': {
              'borderRadius': 0,
              'height': '48px',
              'alignItems': 'center',
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '150%',
              color: '#2D2925',
              padding: '0 4px 0 4px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#2D2925B8',
              opacity: 1,
            },
          }}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              autoComplete: 'tel-country-code',
              maxLength: MAX_COUNTRY_CODE_DIGITS,
            },
            input: {
              startAdornment: (
                <Typography
                  sx={{
                    color: '#2D2925B8',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    mr: 0.5,
                    ml: 1,
                  }}
                >
                  +
                </Typography>
              ),
              endAdornment: (
                <Box
                  sx={{
                    width: '1px',
                    height: 24,
                    backgroundColor: '#DED8D7',
                    ml: 0.5,
                  }}
                />
              ),
              sx: {
                borderRadius: 0,
                height: '48px',
                alignItems: 'center',
                px: 1,
              },
            },
          }}
        />
        <TextField
          fullWidth
          type='tel'
          placeholder={questionData?.placeholder || translate('form.IntakeForms.Contact.Questions.PhoneNumber.Placeholder')}
          value={formatLocalPhoneNumber(parsed.localDigits, parsed.hasOpenParen, parsed.hasCloseParen, parsed.hasHyphen)}
          onChange={handleLocalNumberChange}
          helperText={questionData.helpText}
          sx={{
            '& .MuiOutlinedInput-root': {
              'borderRadius': 0,
              'height': '48px',
              'alignItems': 'center',
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '150%',
              color: '#2D2925',
              fontSize: '16px',
              lineHeight: '150%',
              color: '#2D2925',
              padding: '0 12px 0 6px',
              inputMode: 'tel',
              autoComplete: 'tel-national',
            },
            'input': {
              sx: {
                borderRadius: 0,
                height: '48px',
                alignItems: 'center',
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

PhoneNumberInput.propTypes = {
  questionData: PropTypes.shape({
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PhoneNumberInput;
